/*
 * Landing sections (sections/landing-*.liquid).
 *
 * <landing-film> (film hero): a muted looping clip over the hero's still. The still is the LCP image,
 * so the clip is attached only after the still has loaded and fades in on its first frame. Phones and
 * desktop each play their own clip (data-src-mobile / data-src-desktop). Out of view or in a hidden tab
 * the clip pauses; reduced motion and data saver leave the still, with the button offering Play. The
 * visitor's Play/Pause choice sticks. The theme's pauseAllMedia()/unpauseAllMedia() (global.js) pause and
 * play every <video> when one of its popups opens or closes, so script calls go through instance shims
 * and only this element decides; its own calls use the prototype methods.
 *
 * <landing-rail> (style rail): previous/next arrows, one dot per card and a "01 / 03" counter that
 * follow the scroll position; arrow keys move between cards when one has focus. Layout "Posters"
 * (.ld-rail--poster) opens on the middle card and marks every card data-pos = before | active | after
 * and --ld-d (cards away from the middle one) from the scroll position on each frame, so the card
 * passing the middle grows as it arrives. A mouse click or tap on a poster beside the middle one brings
 * it to the middle; keyboard Enter and modified clicks (new tab) still follow its link.
 *
 * <landing-scroller> (anchor bar): marks its [data-scroller] row with data-overflow = none | end | start |
 * both, so CSS can fade the edge that hides more links when the row scrolls sideways.
 */
(function () {
  if (!window.customElements) return;

  var rawPlay = HTMLMediaElement.prototype.play;
  var rawPause = HTMLMediaElement.prototype.pause;

  function listen(mq, fn, on) {
    if (!mq) return;
    if (mq.addEventListener) mq[on ? 'addEventListener' : 'removeEventListener']('change', fn);
    else if (mq.addListener) mq[on ? 'addListener' : 'removeListener'](fn);
  }

  class LandingFilm extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('.ld-hero__video');
      this.button = this.querySelector('.ld-hero__toggle');
      if (!this.video || !this.button || !window.matchMedia) return;
      this.img = this.querySelector('.ld-hero__media img');
      this.phone = window.matchMedia('(max-width: 749px)');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.saveData = !!(navigator.connection && navigator.connection.saveData);
      this.intent = null; // null: automatic; true: the visitor pressed Play; false: Pause
      this.inView = true;
      this.current = '';
      this.apply = this.apply.bind(this);
      this.boot = this.boot.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPlaying = this.onPlaying.bind(this);
      this.onPause = this.onPause.bind(this);

      var self = this;
      var video = this.video;
      video.play = function () {
        return self.current && self.wanted() ? rawPlay.call(video) : Promise.resolve();
      };
      video.pause = function () {};

      video.addEventListener('playing', this.onPlaying);
      video.addEventListener('pause', this.onPause);
      this.button.addEventListener('click', this.onClick);
      listen(this.phone, this.apply, true);
      listen(this.reduced, this.apply, true);
      document.addEventListener('visibilitychange', this.apply);

      if (!this.img || this.img.complete) this.boot();
      else {
        this.img.addEventListener('load', this.boot, { once: true });
        this.img.addEventListener('error', this.boot, { once: true });
      }
    }

    disconnectedCallback() {
      if (!this.video || !this.button) return;
      if (this.observer) this.observer.disconnect();
      listen(this.phone, this.apply, false);
      listen(this.reduced, this.apply, false);
      document.removeEventListener('visibilitychange', this.apply);
      this.video.removeEventListener('playing', this.onPlaying);
      this.video.removeEventListener('pause', this.onPause);
      this.button.removeEventListener('click', this.onClick);
    }

    boot() {
      if (this.booted) return;
      this.booted = true;
      if ('IntersectionObserver' in window) {
        var self = this;
        this.observer = new IntersectionObserver(function (entries) {
          self.inView = entries[0].isIntersecting;
          self.apply();
        });
        this.observer.observe(this);
      } else {
        this.apply();
      }
    }

    src() {
      return (this.phone.matches ? this.video.getAttribute('data-src-mobile') : this.video.getAttribute('data-src-desktop')) || '';
    }

    wanted() {
      return this.inView && !document.hidden && (this.intent === true || (this.intent === null && !this.reduced.matches && !this.saveData));
    }

    label() {
      var playing = !!this.current && !this.video.paused;
      var text = this.button.getAttribute(playing ? 'data-label-pause' : 'data-label-play');
      if (text && this.button.textContent !== text) this.button.textContent = text;
    }

    attach(url) {
      this.current = url;
      this.classList.remove('is-playing');
      this.video.src = url;
    }

    detach() {
      if (this.current) {
        rawPause.call(this.video);
        this.video.removeAttribute('src');
        this.video.load();
        this.current = '';
      }
      this.classList.remove('is-playing');
    }

    play() {
      var self = this;
      this.video.muted = true;
      var promise = rawPlay.call(this.video);
      if (promise && promise.catch) promise.catch(function () { self.label(); });
    }

    apply() {
      var url = this.src();
      this.button.hidden = !url;
      if (!url) {
        this.detach();
        this.label();
        return;
      }
      if (this.wanted()) {
        if (url !== this.current) this.attach(url);
        this.play();
        return;
      }
      if (this.current && this.current !== url) this.detach(); // the other breakpoint's paused clip must not cover this still
      if (this.current && !this.video.paused) rawPause.call(this.video);
      else this.label();
    }

    onClick() {
      this.intent = !this.current || this.video.paused;
      if (this.intent) {
        var url = this.src();
        if (url !== this.current) this.attach(url);
        this.play();
      } else {
        rawPause.call(this.video);
      }
    }

    onPlaying() {
      this.classList.add('is-playing');
      this.label();
    }

    onPause() {
      this.label();
    }
  }

  class LandingRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.ld-rail__track');
      this.items = [].slice.call(this.querySelectorAll('.ld-rail__item'));
      this.dots = [].slice.call(this.querySelectorAll('.ld-rail__dot'));
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.count = this.querySelector('[data-rail-count]');
      if (!this.track || !this.items.length) return;
      this.reduced = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
      this.poster = this.classList.contains('ld-rail--poster');
      this.selected = 0;
      this.timer = null;
      this.frame = null;
      this.onScroll = this.onScroll.bind(this);
      this.onKey = this.onKey.bind(this);

      var self = this;
      if (this.prev) this.prev.addEventListener('click', function () { self.select(self.selected - 1); });
      if (this.next) this.next.addEventListener('click', function () { self.select(self.selected + 1); });
      this.dots.forEach(function (dot, index) {
        dot.addEventListener('click', function () { self.select(index); });
      });
      if (this.poster) {
        this.items.forEach(function (item, index) {
          var link = item.querySelector('a');
          if (!link) return;
          link.addEventListener('click', function (event) {
            if (item.getAttribute('data-pos') === 'active' || event.detail === 0) return;
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            self.select(index);
          });
        });
      }
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKey);
      if (this.poster) {
        this.selected = Math.floor((this.items.length - 1) / 2);
        this.track.scrollLeft = this.offset(this.selected);
        this.paint(this.selected);
      }
      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      this.track.removeEventListener('keydown', this.onKey);
      clearTimeout(this.timer);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    offset(index) {
      return this.items[index].offsetLeft - this.items[0].offsetLeft;
    }

    max() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    select(index) {
      this.selected = Math.max(0, Math.min(this.items.length - 1, index));
      var left = Math.min(this.max(), this.offset(this.selected));
      var smooth = !(this.reduced && this.reduced.matches);
      if (this.track.scrollTo) this.track.scrollTo({ left: left, behavior: smooth ? 'smooth' : 'auto' });
      else this.track.scrollLeft = left;
      this.update();
    }

    // the card whose start is nearest to the scroll position; near the end several cards share the
    // clamped position, so a selection that already sits there is kept
    nearest() {
      var x = this.track.scrollLeft;
      var max = this.max();
      var self = this;
      if (Math.abs(Math.min(max, this.offset(this.selected)) - x) <= 2) return this.selected;
      var best = 0;
      this.items.forEach(function (item, index) {
        if (Math.abs(Math.min(max, self.offset(index)) - x) < Math.abs(Math.min(max, self.offset(best)) - x)) best = index;
      });
      return best;
    }

    onScroll() {
      var self = this;
      if (this.poster && !this.frame) {
        this.frame = requestAnimationFrame(function () {
          self.frame = null;
          self.paint(self.nearest());
        });
      }
      clearTimeout(this.timer);
      this.timer = setTimeout(function () {
        self.selected = self.nearest();
        self.update();
      }, 120);
    }

    onKey(event) {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      var to = this.selected + (event.key === 'ArrowRight' ? 1 : -1);
      this.select(to);
      var link = this.items[this.selected].querySelector('a');
      if (link && this.contains(document.activeElement)) link.focus({ preventScroll: true });
    }

    // posters: the card in the middle is full size; CSS shrinks the others and tucks them under it
    paint(index) {
      this.items.forEach(function (item, i) {
        var pos = i < index ? 'before' : i > index ? 'after' : 'active';
        var d = String(Math.abs(i - index));
        if (item.getAttribute('data-pos') !== pos) item.setAttribute('data-pos', pos);
        if (item.style.getPropertyValue('--ld-d') !== d) item.style.setProperty('--ld-d', d);
      });
    }

    update() {
      var self = this;
      var last = this.items.length - 1;
      this.dots.forEach(function (dot, index) {
        if (index === self.selected) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (this.count) this.count.textContent = (this.selected < 9 ? '0' : '') + (this.selected + 1);
      if (this.prev) this.prev.disabled = this.selected === 0;
      if (this.next) this.next.disabled = this.selected === last;
    }
  }

  class LandingScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-scroller]');
      if (!this.track) return;
      this.update = this.update.bind(this);
      this.track.addEventListener('scroll', this.update, { passive: true });
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.update);
        this.observer.observe(this.track);
      } else {
        window.addEventListener('resize', this.update);
      }
      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.update);
      if (this.observer) this.observer.disconnect();
      else window.removeEventListener('resize', this.update);
    }

    update() {
      var track = this.track;
      var max = track.scrollWidth - track.clientWidth;
      var state = 'none';
      if (max > 1) {
        if (track.scrollLeft <= 1) state = 'end';
        else if (track.scrollLeft >= max - 1) state = 'start';
        else state = 'both';
      }
      if (track.getAttribute('data-overflow') !== state) track.setAttribute('data-overflow', state);
    }
  }

  if (!customElements.get('landing-film')) customElements.define('landing-film', LandingFilm);
  if (!customElements.get('landing-scroller')) customElements.define('landing-scroller', LandingScroller);
  if (!customElements.get('landing-rail')) customElements.define('landing-rail', LandingRail);
})();
